import { atom, useAtom, useSetAtom } from 'jotai';
import { EntityKey, gridAtom } from './grid';

export const collapsedGroupsAtom = atom<Set<EntityKey>>(new Set<EntityKey>());

export type TreeNodeMeta = {
  depth: number;
  numChildren: number;
  hasDescendantRows: boolean;
  groupDepth: number;
  groupHeight: number;
};

export const treeStructureAtom = atom<Record<EntityKey, TreeNodeMeta>>((get) => {
  const { entities, childrenMap, rootIds } = get(gridAtom);
  const result: Record<EntityKey, TreeNodeMeta> = {};

  const computeHasDescendantRows = (key: EntityKey): boolean => {
    const children = childrenMap[key] || [];
    for (const childKey of children) {
      const ref = entities[childKey];
      if (ref.type === 'ROW') return true;
      if (computeHasDescendantRows(childKey)) return true;
    }
    return false;
  };

  const computeGroupHeight = (key: EntityKey): number => {
    const children = childrenMap[key] || [];
    const groupChildren = children.filter((childKey) => entities[childKey]?.type === 'GROUP');
    if (groupChildren.length === 0) {
      return 1;
    }

    const childHeights = groupChildren.map((childKey) => computeGroupHeight(childKey));
    return Math.max(...childHeights) + 1;
  };

  const traverse = (key: EntityKey, depth: number, parentGroupDepth: number = 0) => {
    const children = childrenMap[key] || [];
    const ref = entities[key];
    const groupDepth = ref.type === 'ROW' ? parentGroupDepth : parentGroupDepth + 1;
    result[key] = {
      depth,
      numChildren: children.length,
      groupDepth,
      groupHeight: ref.type === 'ROW' ? 0 : computeGroupHeight(key),
      hasDescendantRows: ref.type === 'ROW' || computeHasDescendantRows(key),
    };
    children.forEach((child) => traverse(child, depth + 1, groupDepth));
  };

  rootIds.forEach((key) => traverse(key, 0));
  return result;
});

export const visibleKeysAtom = atom<EntityKey[]>((get) => {
  const { rootIds, childrenMap } = get(gridAtom);
  const collapsed = get(collapsedGroupsAtom);
  const result: EntityKey[] = [];

  const traverse = (ids: EntityKey[]) => {
    for (const key of ids) {
      result.push(key);
      const children = childrenMap[key] || [];
      if (!collapsed.has(key) && children.length > 0) {
        traverse(children);
      }
    }
  };

  traverse(rootIds);
  return result;
});

export const maxDepthAtom = atom<number>((get) => {
  const structure = get(treeStructureAtom);
  return Math.max(0, ...Object.values(structure).map((m) => m.depth));
});

export function useToggleGroup(key: EntityKey) {
  const setCollapsed = useSetAtom(collapsedGroupsAtom);
  const toggleGroup = () => {
    setCollapsed((current) => {
      const newSet = new Set(current);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };
  return toggleGroup;
}

type ToggleState = 'expanded' | 'collapsed' | 'mixed' | 'no-groups';
const allGroupsKeysAtom = atom<EntityKey[]>((get) => {
  const structure = get(treeStructureAtom);
  const entities = get(gridAtom).entities;

  return Object.keys(structure).filter((key) => {
    const meta = structure[key];
    return entities[key]?.type === 'GROUP' && meta.numChildren > 0;
  });
});

export const allExpandedAtom = atom<ToggleState, [], void>(
  (get) => {
    const groupKeys = get(allGroupsKeysAtom);
    const collapsed = get(collapsedGroupsAtom);

    if (groupKeys.length === 0) return 'no-groups';

    // Intersection count: How many VISIBLE groups are collapsed?
    let collapsedCount = 0;
    for (const key of groupKeys) {
      if (collapsed.has(key)) collapsedCount++;
    }

    if (collapsedCount === 0) return 'expanded';
    if (collapsedCount === groupKeys.length) return 'collapsed';
    return 'mixed';
  },
  (get, set) => {
    const currentState = get(allExpandedAtom);
    const groupKeys = get(allGroupsKeysAtom);

    if (currentState === 'collapsed' || currentState === 'mixed') {
      set(collapsedGroupsAtom, new Set());
    } else {
      set(collapsedGroupsAtom, new Set(groupKeys));
    }
  },
);

export function useToggleAll(): [state: ToggleState, toggle: () => void] {
  return useAtom(allExpandedAtom);
}
